import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { DeleteOutline as DeleteOutlineIcon, Inbox as InboxIcon } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';
import { getApiOrigin } from '../config/environment';

type ImageModerationAlertItem = {
  id: number;
  status: string;
  nsfw_score: number;
  reason?: string | null;
  created_at: string;
  trashed_at?: string | null;
  conversation_id?: number | null;
  metadata?: Record<string, unknown> | null;
  user?: {
    id?: number | null;
    email?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    is_active?: boolean | null;
  } | null;
};

const POLL_INTERVAL_MS = 60_000;

function getAlertImageUrl(metadata?: Record<string, unknown> | null): string {
  const absolute = String(metadata?.image_url || '').trim();
  if (absolute) return absolute;

  const stored = String(metadata?.stored_filename || '').trim();
  if (stored) {
    return `${getApiOrigin()}/uploads/moderation-alerts/${encodeURIComponent(stored)}`;
  }

  return '';
}

const ImageModerationAlertsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [view, setView] = useState<'active' | 'trash'>('active');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['image-moderation-alerts', view],
    queryFn: async () => {
      const res = await apiService.getImageModerationAlerts({
        page: 1,
        limit: 100,
        in_trash: view === 'trash' ? 1 : 0,
      });
      return res.data as { alerts: ImageModerationAlertItem[] };
    },
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    staleTime: POLL_INTERVAL_MS,
  });

  const invalidateAlerts = async () => {
    await queryClient.invalidateQueries({ queryKey: ['image-moderation-alerts'] });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Impossible de charger les alertes de moderation image.</Alert>;
  }

  const alerts = data?.alerts ?? [];

  const handleBanUser = async (alertId: number, userId?: number | null) => {
    if (!userId) return;
    if (!window.confirm('Confirmer le bannissement (désactivation) de cet utilisateur ?')) return;
    try {
      await apiService.toggleUserStatus(
        userId,
        'Compte desactive suite a une alerte de moderation image (contenu potentiellement inapproprie).'
      );
      await apiService.trashImageModerationAlert(alertId);
      await invalidateAlerts();
      await refetch();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      window.alert("Bannissement ou archivage de l'alerte a echoue. Verifiez la console reseau.");
    }
  };

  const handleApproveAlert = async (alertId: number) => {
    const reason = window.prompt('Commentaire moderation (optionnel) :') || undefined;
    await apiService.reviewImageModerationAlert(alertId, { action: 'approve', reason });
    await invalidateAlerts();
    await refetch();
  };

  const handleUnapproveAlert = async (alertId: number) => {
    const reason = window.prompt("Raison de l'annulation (optionnel) :") || undefined;
    await apiService.reviewImageModerationAlert(alertId, { action: 'unapprove', reason });
    await invalidateAlerts();
    await refetch();
  };

  const handleRestoreAlert = async (alertId: number) => {
    await apiService.restoreImageModerationAlert(alertId);
    await invalidateAlerts();
    await refetch();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Alertes moderation image
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Images bloquees par le detecteur NSFW du front utilisateur. Apres bannissement, l’alerte est deplacee dans la
        corbeille.
      </Typography>

      <Tabs
        value={view}
        onChange={(_, v) => setView(v as 'active' | 'trash')}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="active" label="Alertes actives" icon={<InboxIcon />} iconPosition="start" />
        <Tab value="trash" label="Corbeille" icon={<DeleteOutlineIcon />} iconPosition="start" />
      </Tabs>

      {alerts.length === 0 ? (
        <Alert severity="success">
          {view === 'active' ? 'Aucune alerte pour le moment.' : 'La corbeille est vide.'}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {alerts.map((alertItem) =>
            (() => {
              const alertImageUrl = getAlertImageUrl(alertItem.metadata);
              const isTrash = view === 'trash';
              return (
                <Grid item xs={12} md={6} key={alertItem.id}>
                  <Card>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Alerte #{alertItem.id}
                        </Typography>
                        <Chip
                          size="small"
                          label={alertItem.status}
                          color={alertItem.status === 'pending' ? 'warning' : 'default'}
                        />
                      </Box>
                      {isTrash && alertItem.trashed_at ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          Mis en corbeille : {new Date(alertItem.trashed_at).toLocaleString('fr-FR')}
                        </Typography>
                      ) : null}
                      <Typography variant="body2" color="text.secondary">
                        Score NSFW: {Number(alertItem.nsfw_score || 0).toFixed(3)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Utilisateur: {alertItem.user?.email || 'inconnu'}
                        {alertItem.user?.is_active === false ? (
                          <Chip size="small" label="Compte desactive" color="error" sx={{ ml: 1 }} />
                        ) : null}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Conversation: {alertItem.conversation_id ?? 'n/a'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Date: {new Date(alertItem.created_at).toLocaleString('fr-FR')}
                      </Typography>
                      {alertItem.reason ? (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Motif: {alertItem.reason}
                        </Typography>
                      ) : null}
                      <Divider sx={{ my: 1.5 }} />
                      <Typography variant="caption" color="text.secondary">
                        Image: {(alertItem.metadata?.file_name as string) || 'n/a'} -{' '}
                        {String(alertItem.metadata?.mime_type || 'n/a')}
                      </Typography>
                      <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={!alertImageUrl}
                          onClick={() => {
                            if (alertImageUrl) window.open(alertImageUrl, '_blank', 'noopener,noreferrer');
                          }}
                        >
                          Voir l'image
                        </Button>
                        {!isTrash ? (
                          <>
                            <Button
                              size="small"
                              color="success"
                              variant="contained"
                              disabled={alertItem.status === 'approved'}
                              onClick={() => handleApproveAlert(alertItem.id)}
                            >
                              Reafficher (valider)
                            </Button>
                            <Button
                              size="small"
                              color="warning"
                              variant="contained"
                              disabled={alertItem.status !== 'approved'}
                              onClick={() => handleUnapproveAlert(alertItem.id)}
                            >
                              Annuler validation
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              variant="contained"
                              disabled={!alertItem.user?.id}
                              onClick={() => handleBanUser(alertItem.id, alertItem.user?.id)}
                            >
                              Bannir la personne
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="small"
                            color="primary"
                            variant="contained"
                            onClick={() => handleRestoreAlert(alertItem.id)}
                          >
                            Restaurer (retirer de la corbeille)
                          </Button>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })()
          )}
        </Grid>
      )}
    </Box>
  );
};

export default ImageModerationAlertsPage;
